// Money is stored as integer cents on server. Helpers convert for display.

import { useCurrencyStore, getCurrencySymbol } from '@/stores/currency.js';

export { getCurrencySymbol };

export const centsToRupees = (cents) => (Number(cents || 0) / 100);
export const rupeesToCents = (rupees) => Math.round(Number(rupees || 0) * 100);

/**
 * Build an Intl.NumberFormat for the given currency code.
 * Cached by currency to avoid recreating on every call.
 */
const fmtCache = new Map();
const getCurrencyFormatter = (currency) => {
  const c = currency || 'LKR';
  if (!fmtCache.has(c)) {
    fmtCache.set(c, new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: c,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }));
  }
  return fmtCache.get(c);
};

const getNumberFormatter = () => {
  if (!fmtCache.has('__NUM__')) {
    fmtCache.set('__NUM__', new Intl.NumberFormat('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }));
  }
  return fmtCache.get('__NUM__');
};

export const formatLKR = (cents) => {
  const symbol = getCurrencySymbol();
  return `${symbol} ${getNumberFormatter().format(centsToRupees(cents))}`;
};

export const formatNumber = (cents) =>
  getNumberFormatter().format(centsToRupees(cents));

export const formatDate = (d) => {
  if (!d) return '';
  // Plain YYYY-MM-DD strings must be split directly to avoid UTC→local day shift
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [year, month, day] = d.split('-');
    return `${day}/${month}/${year}`;
  }
  // Numeric Unix timestamps (ms or s)
  if (typeof d === 'number') {
    const dt = new Date(d > 1e10 ? d : d * 1000);
    if (Number.isNaN(dt.getTime())) return '';
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  }
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (!dt || typeof dt.getTime !== 'function' || Number.isNaN(dt.getTime())) return '';
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = dt.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatDateTime = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '';
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  const ss = String(dt.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hh}:${mm}:${ss}`;
};

/** Convert decimal hours to HH:MM string (e.g. 1.5 → "01:30") */
export const formatHours = (h) => {
  const total = Math.round(Number(h || 0) * 60);
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

export const monthName = (m) =>
  new Date(2000, (Number(m) || 1) - 1, 1).toLocaleString('en-LK', { month: 'long' });

export const periodLabel = (year, month) => `${monthName(month)} ${year}`;